import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { familiesApi, Family } from '../api/families';
import { sharingApi, SharedMemberItem } from '../api/sharing';
import FamilyNameFormModal from '../components/FamilyNameFormModal';
import PageBar from '../components/PageBar';
import { useAuth } from '../contexts/AuthContext';
import './AdminConfig.css';

interface FamiliesFlashState {
  flash?: string;
}

const IconHome = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 9.5 12 3l9 6.5V21a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);

const IconPlus = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconRefresh = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const Families: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [families, setFamilies] = useState<Family[]>([]);
  const [inboundShared, setInboundShared] = useState<SharedMemberItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  // One-shot success banner consumed from `location.state.flash`
  // (set by e.g. FamilyDetail after a successful delete-family). We
  // clear the state on mount so reloading the page doesn't show the
  // banner again and the message auto-fades after ~6 s.
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as FamiliesFlashState | null;
    if (state?.flash) {
      setFlash(state.flash);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 6000);
    return () => window.clearTimeout(t);
  }, [flash]);

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    sharingApi.listInboundMembers().then(setInboundShared).catch(() => setInboundShared([]));
  }, []);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setShowCreateModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadFamilies = async () => {
    try {
      setFamilies(await familiesApi.getFamilies());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la incarcarea familiilor');
    } finally {
      setLoading(false);
    }
  };

  const reloadInbound = async () => {
    try {
      setInboundShared(await sharingApi.listInboundMembers());
    } catch {
      setInboundShared([]);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([loadFamilies(), reloadInbound()]);
  };

  // Reused for both the loading shell and the populated state so the
  // page-bar is rendered identically while families are still fetching.
  // Crumbs: Dashboard / Familii (last is leaf, non-clickable).
  const pageBar = (
    <PageBar
      ariaLabel="Navigare familii"
      crumbs={[
        { label: 'Dashboard', to: '/dashboard', icon: <IconHome /> },
        { label: 'Familii' },
      ]}
      backTo="/dashboard"
      primaryAction={{
        label: 'Familie nouă',
        icon: <IconPlus />,
        onClick: () => setShowCreateModal(true),
      }}
      menuItems={[
        {
          label: 'Reîncarcă',
          icon: <IconRefresh />,
          onClick: handleRefresh,
          disabled: loading,
        },
      ]}
    />
  );

  if (loading) {
    return (
      <div className="page-bar-page">
        {pageBar}
        <div className="page-container admin-page page-bar-page-content">
          <div className="admin-hero-skeleton" />
          <div className="grid grid-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton skeleton-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-bar-page">
      {pageBar}

      <div className="page-container admin-page page-bar-page-content">
        {flash && (
          <div
            className="alert alert-success"
            role="status"
            style={{ marginBottom: 'var(--sp-3)' }}
          >
            {flash}
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        <FamilyNameFormModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Familie nouă"
          initialName=""
          submitLabel="Creează"
          onSubmit={async (name) => {
            await familiesApi.createFamily({ name });
            await loadFamilies();
          }}
        />

        {families.length === 0 && inboundShared.length === 0 ? (
          <div className="card">
            <div className="empty-state">
              <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
              <div className="empty-state-title">Nicio familie</div>
              <div className="empty-state-text">Creează o familie pentru a începe.</div>
              <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                <IconPlus />
                Familie nouă
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-2">
            {families.map((family) => (
              <Link key={family.id} to={`/families/${family.id}`} style={{ textDecoration: 'none' }}>
                <div className="card card-interactive">
                  <div className="flex items-center justify-between">
                    <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-slate-800)' }}>
                      {family.name}
                      {user && family.owner_id !== user.id && (
                        <span
                          className="badge"
                          style={{ marginLeft: 8, fontSize: '0.7rem', verticalAlign: 'middle', fontWeight: 500 }}
                        >
                          Partajat
                        </span>
                      )}
                    </h3>
                    <span className="badge badge-teal">
                      {family.members.length} {family.members.length === 1 ? 'membru' : 'membri'}
                    </span>
                  </div>
                  <div style={{ marginTop: 'var(--sp-3)', fontSize: 'var(--text-sm)', color: 'var(--color-slate-500)' }}>
                    Creat {new Date(family.created_at).toLocaleDateString('ro-RO')}
                  </div>
                </div>
              </Link>
            ))}
            {inboundShared.length > 0 && (
              <Link to="/shared-members" style={{ textDecoration: 'none' }}>
                <div className="card card-interactive">
                  <div className="flex items-center justify-between">
                    <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-slate-800)' }}>
                      Membri partajați
                    </h3>
                    <span className="badge badge-teal">
                      {inboundShared.length} {inboundShared.length === 1 ? 'persoană' : 'persoane'}
                    </span>
                  </div>
                  <div style={{ marginTop: 'var(--sp-3)', fontSize: 'var(--text-sm)', color: 'var(--color-slate-500)' }}>
                    Acces primit la nivel de membru
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Families;
